<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    type PaletteColorProp,
    type IconSize,
    type SizeConditional,
    useExtractSize,
} from '#imports';

interface IProps {
    color?: PaletteColorProp | string;
    size?: SizeConditional<IconSize>;
    icon: IconDefinition;
    rotation?: 90 | 180 | 270 | '90' | '180' | '270';
    swapOpacity?: boolean;
    primaryColor?: string;
    secondaryColor?: string;
    secondaryOpacity?: number;
    inverse?: boolean;
    bounce?: boolean;
    shake?: boolean;
    beatFade?: boolean;
    spin?: boolean;
    spinPulse?: boolean;
    spinReverse?: boolean;
    transform?: object | string;
    pulse?: boolean;
    beat?: boolean;
    fade?: boolean;
    fixedWidth?: boolean;
    flash?: boolean;
    flip?: 'horizontal' | 'vertical' | 'both';
}
const {
    color = 'inherit',
    size = 'medium',
    flash,
    icon,
    primaryColor,
    secondaryColor,
    secondaryOpacity = 0.4,
    transform,
} = defineProps<IProps>();
const { mobile, desktop } = useExtractSize(() => size, 'medium');
</script>
<template>
    <span class="mr-icon-container" :class="[desktop, `mobile-${mobile}`]">
        <FontAwesomeIcon
            :size="undefined"
            :icon
            :style="{
                color,
                '--fa-primary-color': primaryColor,
                '--fa-secondary-color': secondaryColor,
                '--fa-secondary-opacity': secondaryOpacity,
            }"
            class="mr-icon-svg"
            :rotation
            :fixed-width
            :flip
            :pulse
            :flash
            :swap-opacity
            :spin
            :spin-pulse
            :transform
            :inverse
            :bounce
            :shake
            :beat
            :fade
            :beat-fade
            :spin-reverse
            :secondary-opacity
        />
    </span>
</template>

<style scoped lang="scss">
@mixin x-large() {
    width: 48px;
    height: 48px;
    font-size: 28px;
}
@mixin large() {
    width: 32px;
    height: 32px;
    font-size: 22px;
}
@mixin medium() {
    width: 24px;
    height: 24px;
    font-size: 18px;
}
@mixin small() {
    width: 20px;
    height: 20px;
    font-size: 16px;
}
@mixin x-small() {
    width: 20px;
    height: 20px;
    font-size: 14px;
}
.mr-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &::before {
        color: inherit;
    }

    // size
    &.x-large {
        @include x-large();
    }
    &.large {
        @include large();
    }
    &.medium {
        @include medium();
    }
    &.small {
        @include small();
    }
    &.x-small {
        @include x-small();
    }

    @include mobile-only() {
        // size
        &.mobile-x-large {
            @include x-large();
        }
        &.mobile-large {
            @include large();
        }
        &.mobile-medium {
            @include medium();
        }
        &.mobile-small {
            @include small();
        }
        &.mobile-x-small {
            @include x-small();
        }
    }
}
</style>
